<template>
  <teleport to="body">
    <div class="toast-container">
      <transition-group name="toast-slide">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          :class="['toast', `toast-${notification.type}`]"
          @click="handleClick(notification)"
        >
          <!-- Icon -->
          <div class="toast-icon">
            <!-- Success Icon -->
            <svg v-if="notification.type === 'success'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <!-- Error Icon -->
            <svg v-else-if="notification.type === 'error'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <!-- Warning Icon -->
            <svg v-else-if="notification.type === 'warning'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <!-- Info Icon -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
          </div>

          <!-- Content -->
          <div class="toast-content">
            <div class="toast-title">{{ notification.title }}</div>
            <div class="toast-body">{{ notification.body }}</div>
            <div v-if="notification.deviceName" class="toast-meta">
              📍 {{ notification.deviceName }}
            </div>
          </div>

          <!-- Close Button -->
          <button
            v-if="notification.dismissible"
            class="toast-close"
            @click.stop="dismiss(notification.id)"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>

          <!-- Progress Bar (for auto-dismiss) -->
          <div
            v-if="notification.duration && notification.duration > 0"
            class="toast-progress"
            :style="{ animationDuration: `${notification.duration}ms` }"
          ></div>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useStore } from 'vuex';

export default defineComponent({
  name: 'ToastNotification',
  setup() {
    const store = useStore();

    const notifications = computed(() => store.getters['notifications/allNotifications']);

    const dismiss = (id: string) => {
      store.dispatch('notifications/remove', id);
    };

    const handleClick = (notification: any) => {
      if (notification.action) {
        notification.action.handler();
        dismiss(notification.id);
      }
    };

    return {
      notifications,
      dismiss,
      handleClick,
    };
  },
});
</script>

<style scoped>
/* Toast Container - Fixed top-right position */
.toast-container {
  position: fixed;
  top: var(--spacing-4);
  right: var(--spacing-4);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  max-width: 420px;
  pointer-events: none;
}

/* Toast Card */
.toast {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-3);
  padding: var(--spacing-4);
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  pointer-events: all;
  cursor: pointer;
  transition: var(--transition-base);
  overflow: hidden;
  min-width: 320px;
}

.toast:hover {
  transform: translateX(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.08);
}

/* Toast Types - Using app's green theme */
.toast-success {
  border-left: 4px solid var(--color-primary);
}

.toast-error {
  border-left: 4px solid var(--color-error);
}

.toast-warning {
  border-left: 4px solid var(--color-warning);
}

.toast-info {
  border-left: 4px solid var(--color-info);
}

/* Toast Icon */
.toast-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.toast-success .toast-icon {
  color: var(--color-primary);
  background: var(--color-primary-alpha-10);
}

.toast-error .toast-icon {
  color: var(--color-error);
  background: var(--color-error-bg);
}

.toast-warning .toast-icon {
  color: var(--color-warning);
  background: var(--color-warning-bg);
}

.toast-info .toast-icon {
  color: var(--color-info);
  background: var(--color-info-bg);
}

.toast-icon svg {
  width: 20px;
  height: 20px;
}

/* Toast Content */
.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-1);
  line-height: 1.3;
}

.toast-body {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-meta {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--spacing-2);
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
}

/* Close Button */
.toast-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--color-gray-500);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  transition: var(--transition-fast);
}

.toast-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--color-gray-700);
}

.toast-close svg {
  width: 18px;
  height: 18px;
}

/* Progress Bar (auto-dismiss indicator) */
.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  transform-origin: left;
  animation: toast-progress linear forwards;
}

.toast-success .toast-progress {
  background: linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
}

.toast-error .toast-progress {
  background: linear-gradient(90deg, var(--color-error) 0%, #b02a37 100%);
}

.toast-warning .toast-progress {
  background: linear-gradient(90deg, var(--color-warning) 0%, #e0a800 100%);
}

.toast-info .toast-progress {
  background: linear-gradient(90deg, var(--color-info) 0%, #0aa2c0 100%);
}

@keyframes toast-progress {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

/* Toast Animations */
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-slide-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-slide-leave-to {
  transform: translateX(120%);
  opacity: 0;
}

.toast-slide-move {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Responsive Design */
@media (max-width: 768px) {
  .toast-container {
    top: 0.5rem;
    right: 0.5rem;
    left: 0.5rem;
    max-width: none;
  }

  .toast {
    min-width: auto;
    width: 100%;
  }
}
</style>

